import _typeof from '@babel/runtime/helpers/esm/typeof';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _objectSpread2 from '@babel/runtime/helpers/esm/objectSpread';
import { compose, applyMiddleware, createStore as createStore$1 } from 'redux';
import thunk from 'redux-thunk';
import { isFSA } from 'flux-standard-action';
import invariant from 'invariant';

function isPromise(val) {
  return val && typeof val.then === 'function';
}
/**
 * Reducer factory that returns a reducer to manage the state of a promise.
 *
 * Combine with promiseMiddleware
 *
 * The managed state has the following fields:
 *
 *
 * inProgress - true while the promise is started, but not resolved (e.g. network call in progress)
 *
 * success - true when the promise has resolved successfully
 *
 * error - truthy when the promise has been rejected. Either literal true or an Error object
 * if available
 *
 * @param type the name of the promise action (e.g. 'SUBMIT_CONTACT')
 * @returns {promiseStatusReducer}
 */


function createPromiseStatusReducer(type) {
  invariant(type, "[standard-redux] promise-status-middleware requires an action type string. You passed \"" + type + "\"");
  return function promiseStatusReducer(state, action) {
    if (state === void 0) {
      state = {
        inProgress: false,
        success: false,
        error: false
      };
    }

    if (action.type === type) {
      return {
        inProgress: action.meta && action.meta.fetching,
        success: action.meta && action.meta.success,
        error: action.error ? action.payload || true : false
      };
    }

    return state;
  };
}
/**
 * Redux middleware that dispatches multiple actions over the life of a promise to track its status.
 *
 * To use, dispatch an action with a string `type` property and a promise `payload` property.
 *
 * Example:
 *
 * dispatch({
 *   type: 'GET_LENDER',
 *   payload: API.MAX.POST({
 *     action: 'getLender',
 *     input: { ... }
 *   })
 * })
 *
 * This will return the promise so additional processing can be done.
 *
 * dispatch({
 *   type: 'GET_LENDER',
 *   payload: API.MAX.POST({
 *     action: 'getLender',
 *     input: { ... }
 *   })
 * }).then(({ error, payload }) => {
 *   if (!error) {
 *     dispatch({ type: 'SET_LENDER', payload: payload.lender });
 *   }
 * })
 *
 * Combine with createPromiseStatusReducer
 *
 * To prevent the 'fetching' action from being fired, pass meta: { progress: false } in
 * your action.
 *
 * @param dispatch
 * @returns {Function}
 */

function promiseStatusMiddleware(_ref) {
  var dispatch = _ref.dispatch;
  return function (next) {
    return function (action) {
      if (!isFSA(action) || !isPromise(action.payload)) {
        return next(action);
      }

      if (!action.meta || action.meta.progress) {
        dispatch(_objectSpread2({}, action, {
          payload: null,
          meta: {
            fetching: true
          }
        }));
      }

      return action.payload.then(function (result) {
        return dispatch(_objectSpread2({}, action, {
          payload: result,
          meta: {
            success: true
          }
        }));
      }, function (error) {
        return dispatch(_objectSpread2({}, action, {
          payload: error,
          error: true
        }));
      });
    };
  };
}
/**
 * A common createStore factory, including the following:
 *
 * - thunk middleware
 * - promise-status-middleware
 *
 * - devtools extension integration
 *
 * @param reducer - you app's root reducer
 * @param extraMiddleware - any custom middlewares your app needs
 * @returns a function to create your store
 */


function createStore(reducer, extraMiddleware, initialState, enhancers, extraThunkArgument) {
  if (extraMiddleware === void 0) {
    extraMiddleware = [];
  }

  if (enhancers === void 0) {
    enhancers = [];
  }

  if (extraThunkArgument === void 0) {
    extraThunkArgument = {};
  }

  var middleware = [thunk.withExtraArgument(extraThunkArgument), promiseStatusMiddleware].concat(_toConsumableArray(extraMiddleware));
  var composeEnhancers = (typeof window === "undefined" ? "undefined" : _typeof(window)) === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
  enhancers.push(applyMiddleware.apply(void 0, _toConsumableArray(middleware)));
  return createStore$1(reducer, initialState, composeEnhancers.apply(void 0, _toConsumableArray(enhancers)));
}

export default createStore;
export { createPromiseStatusReducer, createStore };
